import { useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Gestiona <title>, <meta>, <link rel="canonical"> y JSON-LD por página.
 *
 * Sustituye a react-helmet-async: su paquete declara "sideEffects": false,
 * lo que hace que Webpack elimine por tree-shaking el código que actualiza
 * el <head> en el build de producción de Create React App (funciona en
 * `npm start` pero no en `npm run build`). Este componente no depende de
 * ninguna librería externa y restaura los valores previos al desmontar,
 * para que la navegación entre rutas no deje metadatos de otra página.
 */
function upsertMeta(attrName, attrValue, content) {
  let el = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
  const prevContent = el ? el.getAttribute('content') : null;
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return { el, prevContent, created };
}

function Seo({ title, description, keywords, robots, canonical, meta, jsonLd }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const restorers = [];
    if (description) restorers.push(upsertMeta('name', 'description', description));
    if (keywords) restorers.push(upsertMeta('name', 'keywords', keywords));
    if (robots) restorers.push(upsertMeta('name', 'robots', robots));

    (meta || []).forEach(({ name, property, content }) => {
      if (content == null) return;
      restorers.push(
        name ? upsertMeta('name', name, content) : upsertMeta('property', property, content)
      );
    });

    let canonicalRestore = null;
    if (canonical) {
      let el = document.head.querySelector('link[rel="canonical"]');
      const prevHref = el ? el.getAttribute('href') : null;
      const created = !el;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', canonical);
      canonicalRestore = { el, prevHref, created };
    }

    const jsonLdScripts = jsonLd
      ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean).map((obj) => {
          const s = document.createElement('script');
          s.type = 'application/ld+json';
          s.textContent = JSON.stringify(obj);
          document.head.appendChild(s);
          return s;
        })
      : [];

    return () => {
      document.title = prevTitle;
      restorers.forEach(({ el, prevContent, created }) => {
        if (created) el.remove();
        else if (prevContent !== null) el.setAttribute('content', prevContent);
      });
      if (canonicalRestore) {
        if (canonicalRestore.created) canonicalRestore.el.remove();
        else if (canonicalRestore.prevHref !== null) {
          canonicalRestore.el.setAttribute('href', canonicalRestore.prevHref);
        }
      }
      jsonLdScripts.forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, robots, canonical, JSON.stringify(meta), JSON.stringify(jsonLd)]);

  return null;
}

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  robots: PropTypes.string,
  canonical: PropTypes.string,
  meta: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      property: PropTypes.string,
      content: PropTypes.string
    })
  ),
  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
};

export default Seo;
